import { MotornetVehicleKind } from "../types";

export type MotornetAccessoryKind =
	| 'serie'
	| 'optionals'
	| 'vernici'
	| 'pacchetti'
	| 'accessoriVinNonCorrispondenti';

export type MotornetTipoVincolo = 'esclusione' | 'inclusione' | 'vincolo';

export interface MotornetFormulaNode {
	op: string | null;
	ids?: number[];
	operations?: MotornetFormulaNode[];
}

export interface MotornetAccessory {
	idAccessorio: number;
	idDizionario: number;
	codice: string;
	descrizione: string;
	formulaVincoliStrutturata?: MotornetFormulaNode | null;
	formulaInclusioneStrutturata?: MotornetFormulaNode | null;
	formulaEsclusioneStrutturata?: MotornetFormulaNode | null;
}

export type MotornetAccessoriesResponse = {
	[K in MotornetAccessoryKind]?: MotornetAccessory[];
};

export interface MotornetVehicleValidatedOption {
	idAccessorio: number;
	idDizionario: number;
	codice: string;
	descrizione: string;
	tipoAccessorio: MotornetAccessoryKind;
	tipoVincolo?: MotornetTipoVincolo;
	tipoVincoloArray?: Record<number, MotornetTipoVincolo>;
}

export type MotornetVehicleOptionValidationErrorCode =
	| 'UNKNOWN_OPTION'
	| 'SERIE_NOT_SELECTABLE'
	| 'OPTION_DISABLED_BY_MOTORNET_RULE';

export interface MotornetVehicleOptionValidationError {
	code: MotornetVehicleOptionValidationErrorCode;
	optionId: number;
	message: string;
	option?: MotornetVehicleValidatedOption;
	blockedBy?: MotornetVehicleValidatedOption[];
}

export interface MotornetVehicleOptionValidationResult {
	valid: boolean;
	eurotaxCode: string;
	vehicleKind: MotornetVehicleKind;

	requestedOptionIds: number[];
	selectedOptionIds: number[];

	disabledOptionIds: number[];
	excludedOptionIds: number[];
	includedOptionIds: number[];
	constrainedOptionIds: number[];

	selectedOptions: MotornetVehicleValidatedOption[];
	disabledOptions: MotornetVehicleValidatedOption[];

	errors: MotornetVehicleOptionValidationError[];
}

type WorkingAccessory = MotornetAccessory & {
	tipoAccessorio: MotornetAccessoryKind;
	selected?: boolean;
	tipoVincolo?: MotornetTipoVincolo;
	tipoVincoloArray?: Record<number, MotornetTipoVincolo>;
};

const ACCESSORY_BUCKETS: MotornetAccessoryKind[] = [
	'serie',
	'optionals',
	'vernici',
	'pacchetti',
	'accessoriVinNonCorrispondenti',
];

export function validateMotornetVehicleOptions(input: {
	eurotaxCode: string;
	vehicleKind: MotornetVehicleKind;
	accessories: MotornetAccessoriesResponse;
	selectedOptionIds: number[];
}): MotornetVehicleOptionValidationResult {
	const allAccessories = flattenAccessories(input.accessories);
	const byDizionarioId = new Map<number, WorkingAccessory>();

	for (const accessory of allAccessories) {
		byDizionarioId.set(accessory.idDizionario, accessory);
	}

	const requestedOptionIds = uniqueNumbers(input.selectedOptionIds);
	const errors: MotornetVehicleOptionValidationError[] = [];

	for (const optionId of requestedOptionIds) {
		const accessory = byDizionarioId.get(optionId);

		if (!accessory) {
			errors.push({
				code: 'UNKNOWN_OPTION',
				optionId,
				message: `Optional Motornet non trovato: idDizionario=${optionId}`,
			});
			continue;
		}

		if (accessory.tipoAccessorio === 'serie') {
			errors.push({
				code: 'SERIE_NOT_SELECTABLE',
				optionId,
				option: toValidatedOption(accessory),
				message: `Accessorio di serie non selezionabile: ${formatAccessory(
					accessory
				)}`,
			});
			continue;
		}

		const blockingParents = findBlockingParents(
			accessory.idDizionario,
			allAccessories
		);

		if (blockingParents.length > 0) {
			errors.push({
				code: 'OPTION_DISABLED_BY_MOTORNET_RULE',
				optionId,
				option: toValidatedOption(accessory),
				blockedBy: blockingParents.map(toValidatedOption),
				message: `Optional disabilitato dalle regole: ${formatAccessory(
					accessory
				)}`,
			});
			continue;
		}

		select(accessory, allAccessories);
	}

	const selectedOptions = allAccessories.filter(
		(accessory) => accessory.selected === true
	);

	const tipoVincoloByDizionarioId =
		collectTipoVincoloByDizionarioId(allAccessories);

	const disabledOptionIds = Object.keys(tipoVincoloByDizionarioId)
		.map(Number)
		.filter(Number.isInteger);

	const disabledOptions = disabledOptionIds
		.map((id) => byDizionarioId.get(id))
		.filter(isDefined);

	return {
		valid: errors.length === 0,
		eurotaxCode: input.eurotaxCode,
		vehicleKind: input.vehicleKind,

		requestedOptionIds,
		selectedOptionIds: selectedOptions.map(
			(accessory) => accessory.idDizionario
		),

		disabledOptionIds,
		excludedOptionIds: collectIdsByTipoVincolo(
			tipoVincoloByDizionarioId,
			'esclusione'
		),
		includedOptionIds: collectIdsByTipoVincolo(
			tipoVincoloByDizionarioId,
			'inclusione'
		),
		constrainedOptionIds: collectIdsByTipoVincolo(
			tipoVincoloByDizionarioId,
			'vincolo'
		),

		selectedOptions: selectedOptions.map(toValidatedOption),
		disabledOptions: disabledOptions.map(toValidatedOption),

		errors,
	};
}

function select(
	accessory: WorkingAccessory,
	allAccessories: WorkingAccessory[]
): void {
	const hasFormula =
		!!accessory.formulaVincoliStrutturata ||
		!!accessory.formulaInclusioneStrutturata ||
		!!accessory.formulaEsclusioneStrutturata;

	if (!hasFormula) {
		accessory.selected = true;
		return;
	}

	const esclusione = resolveFormulaDefaultChildren({
		formula: accessory.formulaEsclusioneStrutturata,
		allAccessories,
		isFirst: false,
	});

	const inclusione = resolveFormulaDefaultChildren({
		formula: accessory.formulaInclusioneStrutturata,
		allAccessories,
		isFirst: false,
	});

	const vincolo = resolveFormulaDefaultChildren({
		formula: accessory.formulaVincoliStrutturata,
		allAccessories,
		isFirst: true,
	});

	const tipoVincoloArray: Record<number, MotornetTipoVincolo> = {};

	// qui
	for (const idDizionario of esclusione) {
		tipoVincoloArray[idDizionario] = 'esclusione';
	}

	for (const idDizionario of inclusione) {
		tipoVincoloArray[idDizionario] = 'inclusione';
	}

	for (const idDizionario of vincolo) {
		tipoVincoloArray[idDizionario] = 'vincolo';
	}

	accessory.tipoVincoloArray = tipoVincoloArray;
	accessory.selected = true;

	delete accessory.tipoVincolo;

	// qui: esclusione perde in confronto a inclusione perche' ultimo override
	if (esclusione.length > 0) {
		accessory.tipoVincolo = 'esclusione';
	}

	if (inclusione.length > 0) {
		accessory.tipoVincolo = 'inclusione';
	}

	if (vincolo.length > 0) {
		accessory.tipoVincolo = 'vincolo';
	}
}

function resolveFormulaDefaultChildren(input: {
	formula: MotornetFormulaNode | null | undefined;
	allAccessories: WorkingAccessory[];
	isFirst: boolean;
}): number[] {
	if (!input.formula) {
		return [];
	}

	const selected = new Set<number>();

	visitFormulaNode({
		node: input.formula,
		allAccessories: input.allAccessories,
		selected,
		isFirst: input.isFirst,
		orOperator: false,
		isActiveOr: true,
	});

	return [...selected];
}

function visitFormulaNode(input: {
	node: MotornetFormulaNode;
	allAccessories: WorkingAccessory[];
	selected: Set<number>;
	isFirst: boolean;
	orOperator: boolean;
	isActiveOr?: boolean;
}): void {
	const ids = input.node.ids ?? [];
	const operations = input.node.operations ?? [];
	const currentAccessories = findAccessoriesByDizionarioIds(
		input.allAccessories,
		ids
	);

	if (currentAccessories.length > 0) {
		const operator = input.node.op === 'OR' && ids.length > 1 ? 'OR' : 'AND';

		if (input.orOperator) {
			if (input.isActiveOr) {
				applySelectionGroup(currentAccessories, operator, input.selected);
			}
		} else {
			applySelectionGroup(currentAccessories, operator, input.selected);
		}
	}

	const hasFirstLevelOrBranches =
		input.isFirst && operations.length > 1 && input.node.op === 'OR';

	if (hasFirstLevelOrBranches) {
		operations.forEach((operation, index) => {
			visitFormulaNode({
				node: operation,
				allAccessories: input.allAccessories,
				selected: input.selected,
				isFirst: false,
				orOperator: input.node.op === 'OR' && operations.length > 1,
				isActiveOr: index === 0,
			});
		});

		return;
	}

	operations.forEach((operation) => {
		visitFormulaNode({
			node: operation,
			allAccessories: input.allAccessories,
			selected: input.selected,
			isFirst: false,
			orOperator: input.node.op === 'OR' && operations.length > 1,
			isActiveOr: undefined,
		});
	});
}

function applySelectionGroup(
	accessories: WorkingAccessory[],
	operator: 'AND' | 'OR',
	selected: Set<number>
): void {
	if (operator === 'AND') {
		for (const accessory of accessories) {
			selected.add(accessory.idDizionario);
		}

		return;
	}

	const groupIds = accessories.map((accessory) => accessory.idDizionario);

	for (const id of groupIds) {
		selected.delete(id);
	}

	const firstAccessory = accessories[0]; // qui

	if (firstAccessory) {
		selected.add(firstAccessory.idDizionario);
	}
}

function findAccessoriesByDizionarioIds(
	allAccessories: WorkingAccessory[],
	ids: number[]
): WorkingAccessory[] {
	if (ids.length === 0) {
		return [];
	}

	const wantedIds = new Set(ids);

	return allAccessories.filter((accessory) =>
		wantedIds.has(accessory.idDizionario)
	);
}

function findBlockingParents(
	idDizionario: number,
	allAccessories: WorkingAccessory[]
): WorkingAccessory[] {
	return allAccessories.filter((accessory) => {
		if (!accessory.selected || !accessory.tipoVincoloArray) {
			return false;
		}

		return !!accessory.tipoVincoloArray[idDizionario];
	});
}

function collectTipoVincoloByDizionarioId(
	allAccessories: WorkingAccessory[]
): Record<number, MotornetTipoVincolo> {
	const result: Record<number, MotornetTipoVincolo> = {};

	for (const accessory of allAccessories) {
		if (!accessory.selected || !accessory.tipoVincoloArray) {
			continue;
		}

		Object.assign(result, accessory.tipoVincoloArray); // qui
	}

	return result;
}

function collectIdsByTipoVincolo(
	tipoVincoloByDizionarioId: Record<number, MotornetTipoVincolo>,
	tipoVincolo: MotornetTipoVincolo
): number[] {
	return Object.entries(tipoVincoloByDizionarioId)
		.filter(([, value]) => value === tipoVincolo)
		.map(([id]) => Number(id))
		.filter(Number.isInteger);
}

function flattenAccessories(
	accessories: MotornetAccessoriesResponse
): WorkingAccessory[] {
	const result: WorkingAccessory[] = [];

	for (const tipoAccessorio of ACCESSORY_BUCKETS) {
		const items = accessories[tipoAccessorio] ?? [];

		for (const accessory of items) {
			result.push({
				...accessory,
				tipoAccessorio,
				selected: undefined,
				tipoVincolo: undefined,
				tipoVincoloArray: undefined,
			});
		}
	}

	return result;
}

function toValidatedOption(
	accessory: WorkingAccessory
): MotornetVehicleValidatedOption {
	return {
		idAccessorio: accessory.idAccessorio,
		idDizionario: accessory.idDizionario,
		codice: accessory.codice,
		descrizione: accessory.descrizione,
		tipoAccessorio: accessory.tipoAccessorio,
		tipoVincolo: accessory.tipoVincolo,
		tipoVincoloArray: accessory.tipoVincoloArray,
	};
}

function formatAccessory(accessory: WorkingAccessory): string {
	const code = accessory.codice ? `${accessory.codice} - ` : '';
	return `${code}${accessory.descrizione}`;
}

function uniqueNumbers(values: number[]): number[] {
	const result: number[] = [];
	const seen = new Set<number>();

	for (const value of values) {
		if (!Number.isInteger(value) || seen.has(value)) {
			continue;
		}

		seen.add(value);
		result.push(value);
	}

	return result;
}

function isDefined<T>(value: T | null | undefined): value is T {
	return value !== null && value !== undefined;
}
